import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables');
}

// Ensure URL is properly formatted
const formattedUrl = supabaseUrl.startsWith('https://') ? supabaseUrl : `https://${supabaseUrl}`;

console.log('Initializing Supabase client...');

export const supabase = createClient(formattedUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

console.log('✅ Supabase client initialized');

export interface Profile {
  id: string;
  email: string;
  created_at: string;
  last_login: string;
  display_name?: string | null;
  preferences?: Record<string, any>;
}

export interface LearningSession {
  id: string;
  user_id: string;
  subject: string;
  question: string;
  response: string;
  created_at: string;
  feedback: number | null;
  feedback_comment?: string | null;
}

export interface SubjectProgress {
  id: string;
  user_id: string;
  subject: string;
  questions_asked: number;
  last_activity: string;
  proficiency_level: number;
}

export async function ensureProfile(userId: string, email: string): Promise<void> {
  try {
    // First check if profile exists
    const { data: existingProfile, error: checkError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .single();

    if (checkError && checkError.code !== 'PGRST116') { // PGRST116 means no rows returned
      throw checkError;
    }

    // If profile doesn't exist, create it
    if (!existingProfile) {
      const { error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          email,
          created_at: new Date().toISOString(),
          last_login: new Date().toISOString(),
          preferences: {}
        });

      if (insertError) throw insertError;
    } else {
      // Update last login time
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ last_login: new Date().toISOString() })
        .eq('id', userId);

      if (updateError) throw updateError;
    }
  } catch (error) {
    console.error('Error ensuring profile exists:', error);
    throw error;
  }
}

export async function createLearningSession(session: Omit<LearningSession, 'id' | 'created_at'>) {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('No authenticated user');

    // Ensure profile exists before creating session
    await ensureProfile(user.id, user.email || '');

    // Create the learning session
    const { data, error } = await supabase
      .from('learning_sessions')
      .insert({
        ...session,
        user_id: user.id,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating learning session:', error);
    throw error;
  }
}

export async function updateSessionFeedback(sessionId: string, rating: number, comment?: string) {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;
    if (!user) throw new Error('No authenticated user');

    // Get session details
    const { data: session, error: sessionError } = await supabase
      .from('learning_sessions')
      .select('*')
      .eq('id', sessionId)
      .single();

    if (sessionError) throw sessionError;
    if (!session) throw new Error('Session not found');

    // Insert feedback
    const { data: feedback, error: feedbackError } = await supabase
      .from('feedback')
      .insert({
        user_id: user.id,
        session_id: sessionId,
        rating,
        comment,
        question: session.question,
        response: session.response
      })
      .select()
      .single();

    if (feedbackError) throw feedbackError;
    return feedback;
  } catch (error) {
    console.error('Error updating session feedback:', error);
    throw error;
  }
}

export async function updateSessionTitle(sessionId: string, subject: string) {
  try {
    const { data, error } = await supabase
      .from('learning_sessions')
      .update({ subject })
      .eq('id', sessionId)
      .select('*')
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating session title:', error);
    throw error;
  }
}

export async function deleteSession(sessionId: string) {
  const { error } = await supabase
    .from('learning_sessions')
    .delete()
    .eq('id', sessionId);

  if (error) throw error;
}

export async function getSubjectProgress(userId: string, subject: string) {
  const { data, error } = await supabase
    .from('subject_progress')
    .select()
    .eq('user_id', userId)
    .eq('subject', subject)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return data;
}

export async function updateSubjectProgress(
  userId: string,
  subject: string,
  update: Partial<SubjectProgress>
) {
  const existingProgress = await getSubjectProgress(userId, subject);

  if (!existingProgress) {
    const { data, error } = await supabase
      .from('subject_progress')
      .insert({
        user_id: userId,
        subject,
        questions_asked: 1,
        proficiency_level: 1,
        ...update
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  const { data, error } = await supabase
    .from('subject_progress')
    .update({
      questions_asked: existingProgress.questions_asked + 1,
      last_activity: new Date().toISOString(),
      ...update
    })
    .eq('id', existingProgress.id)
    .select()
    .single();

  if (error) throw error;
  return data;
}